/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.originalSlides = null;
    this.isInitialized = false;
    this.productImageWrapper = null;
    this.swiperNavButtons = [];
    this.resizeHandler = null;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) return;
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.visibilityResizeTimeout);
    this.visibilityResizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
    }, 150);
  }

  /**
   * Find slider controls for this block (nested, linked by ID, or single unassigned in section).
   * @returns {HTMLElement|null}
   */
  findControlsElement() {
    const blockId = this.element.dataset.blockId;
    let controlsElement = this.element.querySelector('[data-slider-control]');

    if (controlsElement) {
      return controlsElement;
    }

    // Controls may be a sibling of .slider-content (e.g. product-list wrapper layout)
    const parent = this.element.parentElement;
    if (parent) {
      controlsElement = parent.querySelector('[data-slider-control]');
      if (controlsElement) {
        return controlsElement;
      }
    }

    if (!blockId) {
      return null;
    }

    const section = this.element.closest('[id^="shopify-section-"]');
    if (!section) {
      return null;
    }

    controlsElement = section.querySelector(
      `[data-slider-control][data-slider-id="${blockId}"]`
    );

    if (!controlsElement) {
      const unassigned = section.querySelectorAll(
        '[data-slider-control]:not([data-slider-id])'
      );
      const sliders = section.querySelectorAll('[data-slider-block]');
      if (unassigned.length === 1 && sliders.length === 1) {
        controlsElement = unassigned[0];
      }
    }

    return controlsElement;
  }

  /**
   * Largest slidesPerView from root options and breakpoints (for loop eligibility).
   * @param {object} options
   * @returns {number}
   */
  #getMaxSlidesPerView(options) {
    const parseSpv = (value) => {
      if (value === 'auto' || value === undefined || value === null) {
        return 1;
      }
      const n = Number(value);
      return Number.isFinite(n) && n > 0 ? n : 1;
    };

    let max = parseSpv(options.slidesPerView);

    if (options.breakpoints) {
      Object.values(options.breakpoints).forEach((bp) => {
        const n = parseSpv(bp?.slidesPerView);
        if (n > max) {
          max = n;
        }
      });
    }

    return max;
  }

  /**
   * Apply mobile/desktop spaceBetween from data attributes and breakpoints.
   * @param {object} options
   */
  #applyResponsiveSpaceBetween(options) {
    const mobile = Number(this.element.dataset.spaceBetweenMobile);
    const desktop = Number(this.element.dataset.spaceBetweenDesktop);

    if (Number.isFinite(mobile)) {
      options.spaceBetween = mobile;
    }

    if (!Number.isFinite(desktop)) {
      return;
    }

    if (options.breakpoints && typeof options.breakpoints === 'object') {
      Object.values(options.breakpoints).forEach((breakpoint) => {
        if (breakpoint && typeof breakpoint === 'object') {
          breakpoint.spaceBetween = desktop;
        }
      });
      return;
    }

    if (!Number.isFinite(mobile)) {
      options.spaceBetween = desktop;
    }
  }

  /**
   * Wire Swiper navigation to prev/next buttons on a controls root.
   * @param {HTMLElement|null} controlsRoot
   * @param {object} customOptions
   * @returns {boolean}
   */
  bindNavigation(controlsRoot, customOptions) {
    if (!controlsRoot || customOptions.navigation === false) {
      return false;
    }

    const nextBtn = controlsRoot.querySelector('.slider-navigation-next');
    const prevBtn = controlsRoot.querySelector('.slider-navigation-prev');

    if (!nextBtn || !prevBtn) {
      return false;
    }

    customOptions.navigation = {
      nextEl: nextBtn,
      prevEl: prevBtn,
    };

    return true;
  }

  #treeDistance(nodeA, nodeB) {
    if (!nodeA || !nodeB) {
      return Infinity;
    }

    if (nodeA === nodeB || nodeA.contains(nodeB) || nodeB.contains(nodeA)) {
      return 0;
    }

    const pathA = [];
    let node = nodeA;
    while (node) {
      pathA.push(node);
      node = node.parentElement;
    }

    node = nodeB;
    while (node) {
      const index = pathA.indexOf(node);
      if (index !== -1) {
        let depth = 0;
        let walk = nodeB;
        while (walk !== node) {
          depth += 1;
          walk = walk.parentElement;
        }
        return index + depth;
      }
      node = node.parentElement;
    }

    return Infinity;
  }

  findClosestImageWrapper() {
    const anchor =
      this.swiperNavButtons[0] ||
      this.controlsElement?.querySelector('.slider-nav-hover') ||
      this.controlsElement;
    const scope = this.swiperEl || this.element;

    const outerSlide =
      scope.querySelector('.swiper-wrapper > .swiper-slide-active') ||
      scope.querySelector('.swiper-wrapper > .swiper-slide');

    if (outerSlide) {
      const activeImage = outerSlide.querySelector('.content-image');
      if (activeImage) {
        return activeImage;
      }
    }

    const images = scope.querySelectorAll('.content-image');
    if (!images.length) {
      return null;
    }

    if (!anchor) {
      return images[0];
    }

    let closest = images[0];
    let minDist = this.#treeDistance(anchor, closest);

    images.forEach((image) => {
      const dist = this.#treeDistance(anchor, image);
      if (dist < minDist) {
        minDist = dist;
        closest = image;
      }
    });

    return closest;
  }

  adjustSwiperNavigationPosition() {
    this.productImageWrapper = this.findClosestImageWrapper();
    const totalHeight = this?.productImageWrapper?.offsetHeight / 2;

    if (!totalHeight) {
      return;
    }

    this.swiperNavButtons.forEach((button) => {
      button.style.top = `${totalHeight}px`;
    });
  }

  setupSwiperNavigationPosition() {
    setTimeout(() => {
      this.adjustSwiperNavigationPosition();

      this.resizeHandler = () => {
        clearTimeout(this.resizeTimeout);

        this.resizeTimeout = setTimeout(() => {
          this.adjustSwiperNavigationPosition();
        }, 200);
      };

      window.addEventListener('resize', this.resizeHandler);
    }, 500);
  }

  teardownSwiperNavigationPosition() {
    if (this.swiper && this.onSlideChange) {
      this.swiper.off('slideChange', this.onSlideChange);
      this.onSlideChange = null;
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    clearTimeout(this.resizeTimeout);
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.slider-content');
    if (!this.contentEl) return;

    const blockId = this.element.dataset.blockId;
    const controlsElement = this.findControlsElement();
    this.controlsElement = controlsElement;

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, controls, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => 
        !excludedTags.includes(el.tagName) && 
        !el.hasAttribute('data-slider-control')
      );
      
      if (slides.length < 1) {
        return;
      }

      this.originalSlides = slides;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper', 'w-full', 'max-w-full', 'h-full');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);
    
    // Re-append controls if they were nested inside the slider
    // (If controls are outside in the section, they stay where they are)
    const wasNested = controlsElement?.parentElement === this.contentEl;
    if (controlsElement && wasNested) {
      this.element.appendChild(controlsElement);
    }

    // Get options from data attribute
    const customOptions = this.element.dataset.swiperOptions 
      ? JSON.parse(this.element.dataset.swiperOptions) 
      : {};

    this.#applyResponsiveSpaceBetween(customOptions);

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 16,
      // Enable touch/drag interactions
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Improve touch responsiveness
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchReleaseOnEdges: false,
    };

    const customPaginationColour = this.element.dataset.paginationColour;

    // Disable loop when there are not enough slides for Swiper loop mode at the largest slidesPerView.
    if (customOptions.loop === true) {
      const maxSlidesPerView = this.#getMaxSlidesPerView(customOptions);
      if (slides.length <= 1 || slides.length <= maxSlidesPerView) {
        customOptions.loop = false;
      }
    }

    // Bind navigation (nested controls, linked block, or section fallback)
    if (!this.bindNavigation(controlsElement, customOptions) && blockId) {
      const section = this.element.closest('[id^="shopify-section-"]');
      const linkedControls = section?.querySelector(
        `[data-slider-control][data-slider-id="${blockId}"]`
      );
      this.bindNavigation(linkedControls, customOptions);
    }

    // Check if slider-controls block is present (already found earlier)
    let controlsPaginationColour = customPaginationColour;
    
    if (controlsElement) {
      // Find pagination element
      const paginationEl = controlsElement.querySelector('.swiper-pagination');
      if (paginationEl) {
        // Get pagination type and colour from data attributes
        const paginationType = paginationEl.dataset.paginationType || 'bullets';
        controlsPaginationColour = paginationEl.dataset.paginationColour || customPaginationColour;
        
        // Override pagination config
        customOptions.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: true,
        };
        
        // Handle custom pagination type - render as styled bars
        if (paginationType === 'custom') {
          customOptions.pagination.renderCustom = (swiper, current, total) => {
            let bullets = '';
            for (let i = 1; i <= total; i++) {
              const isActive = i === current ? 'opacity-100' : 'opacity-50';
              bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${controlsPaginationColour};" data-slide-index="${i}"></span>`;
            }
            return `<div class="flex gap-2 justify-center">${bullets}</div>`;
          };

          this.#bindCustomPaginationClicks();
        }
      }
    } else {
      // Fallback: Handle custom pagination type when no controls block (old behavior)
      if (customOptions.pagination && customOptions.pagination.type === 'custom') {
        customOptions.pagination.renderCustom = (swiper, current, total) => {
          let bullets = '';
          for (let i = 1; i <= total; i++) {
            const isActive = i === current ? 'opacity-100' : 'opacity-50';
            bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${customPaginationColour};" data-slide-index="${i}"></span>`;
          }
          return `<div class="flex gap-2 justify-center">${bullets}</div>`;
        };

        this.#bindCustomPaginationClicks();
      }
    }

    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, {
      ...defaultOptions,
      ...customOptions,
    });

    this.isInitialized = true;

    const navHover = controlsElement?.querySelector('.slider-nav-hover');
    this.swiperNavButtons = navHover
      ? [...navHover.querySelectorAll('.slider-navigation-prev, .slider-navigation-next')]
      : [];

    if (this.swiperNavButtons.length) {
      this.teardownSwiperNavigationPosition();
      this.onSlideChange = () => this.adjustSwiperNavigationPosition();
      this.swiper.on('slideChange', this.onSlideChange);
      this.setupSwiperNavigationPosition();
    }
  }

  #customPaginationClickHandler = null;

  #bindCustomPaginationClicks() {
    this.#unbindCustomPaginationClicks();

    this.#customPaginationClickHandler = (e) => {
      const bullet = e.target.closest('[data-slide-index]');
      if (bullet && this.swiper) {
        const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
        this.swiper.slideTo(index);
      }
    };

    this.element.addEventListener('click', this.#customPaginationClickHandler);
  }

  #unbindCustomPaginationClicks() {
    if (this.#customPaginationClickHandler) {
      this.element.removeEventListener('click', this.#customPaginationClickHandler);
      this.#customPaginationClickHandler = null;
    }
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    this.#unbindCustomPaginationClicks();
    this.teardownSwiperNavigationPosition();

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.visibilityResizeTimeout);
    this.teardownSwiperNavigationPosition();
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = document.querySelectorAll('[data-slider-block]');
  sliders.forEach(slider => {
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

// Re-initialize after AJAX content swaps (related products, filters, search, etc.)
if (ThemeBus) {
  ThemeBus.addEventListener(EVENTS.gridUpdated, () => {
    initSliderBlocks();
  });
}

// Export for potential external use
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;

